import React, {useEffect, useState} from 'react';
import { collection, getDocs } from "firebase/firestore";
import {
    Button,
    Dialog,
    DialogBackdrop,
    DialogPanel, DialogTitle, Input,
    Select
} from "@headlessui/react";
import {
    ArrowPathIcon,
    CheckCircleIcon,
    DocumentArrowDownIcon, DocumentArrowUpIcon,
    ExclamationTriangleIcon,
    IdentificationIcon,
    MinusCircleIcon,
    PlusCircleIcon,
    QueueListIcon,
    StopCircleIcon,
    UserGroupIcon,
    UserPlusIcon,
} from "@heroicons/react/24/outline";
import {db} from "../firebase/firebase";
import {useAuth} from "../contexts/authContext";
import {useDarkMode} from "../contexts/darkModeContext";
import Loading from "./Widgets/Loading";
import CustomerSection from "./Customers/CustomerSection";
import AccountView from "./Customers/AccountView";
import CustomerSearchInput from "./Customers/CustomerSearchInput";
import {useNotification} from "./Notifications/NotificationProvider";
import {numberWithCommas, stateMapping} from "./GLOBAL/GlobalFunctions";
import ActionPillButton from "./Widgets/ActionPillButton";
import CustomButton from "./Widgets/CustomButton";
import CustomerTypeImporter from "./Customers/CustomerTypeImporter";

const PlantCustomers = () => {
    const { currentUser } = useAuth();
    
    const [alertsOn] = useState(() => {
        const storedSettings = localStorage.getItem('alertsOn');
        return storedSettings ? JSON.parse(storedSettings).alertsOn : false; // Default to false if null
    });

    const [status, setStatus] = useState("P");
    // const [existing, setExisting] = useState(false);

    const { darkMode } = useDarkMode();
    const { triggerNotification } = useNotification();

    const [allCustomers, setallCustomers] = useState([]);
    const [filteredallCustomers, setFilteredallCustomers] = useState([]);

    const [allPending, setAllPending] = useState([]);
    const [fileredAllPending, setFilteredAllPending] = useState([]);

    const [allInactive, setAllInactive] = useState([]);
    const [fileredAllInactive, setFilteredAllInactive] = useState([]);

    const [searchQuery, setSearchQuery] = useState("");
    const [reps, setReps] = useState([]);
    
    
    const [open, setOpen] = useState(false);
    

    useEffect(() => {
        const fetchUserName = async () => {
            if (currentUser) {
                try {
                    const storedUserData = localStorage.getItem(`userData_${currentUser.uid}`);
                    if (storedUserData) {
                        const userData = JSON.parse(storedUserData);
                        setReps(userData.reps || []);
                    } else {
                        const userDoc = await db.collection('Users').doc(currentUser.uid).get();
                        if (userDoc.exists) {
                            const userData = userDoc.data();
                            setReps(userData.reps || []);
                            localStorage.setItem(`userData_${currentUser.uid}`, JSON.stringify(userData));
                        } else {
                            console.log('User document not found');
                        }
                    }
                } catch (error) {
                    console.error('Error fetching user data:', error);
                }
            }
        };

        fetchUserName();
    }, [currentUser]);

    useEffect(() => {

        const fetchAllUsersPendingData = async () => {
            try {

                // Check if reps is null or empty
                if (!reps || reps.length === 0) {
                    console.log("Reps is null or empty. Returning an empty array.");
                    setAllPending([]);
                    setFilteredAllPending([]);
                    return;
                }

                const storedPendingData = localStorage.getItem(`allPendingCustomers_${currentUser.uid}`);
                if (storedPendingData) {
                    const pendingData = JSON.parse(storedPendingData);
                    setAllPending(pendingData);
                    setFilteredAllPending(pendingData);
                } else {
                    // Extract user IDs from the reps array
                    const repsIds = reps.map((rep) => rep.id);

                    let allPendingData = [];

                    // Iterate over the allowed user IDs in repsIds
                    const userPromises = repsIds.map(async (userId) => {
                        // Reference to the pending collection for this user
                        const pendingCollectionRef = collection(db, `Customers/${userId}/pending`);
                        const pendingSnapshot = await getDocs(pendingCollectionRef);

                        // Push pending data to the allPendingData array
                        pendingSnapshot.forEach((pendingDoc) => {
                            allPendingData.push({
                                ...pendingDoc.data(),
                                id: pendingDoc.id, // Add document ID
                                salesID: userId, // ADD USER ID
                            });
                        });
                    });

                    // Run all user-level fetches in parallel
                    await Promise.all(userPromises);

                    // Handle empty data scenario
                    if (allPendingData.length > 0) {
                        setAllPending(allPendingData);
                        setFilteredAllPending(allPendingData);
                        localStorage.setItem(`allPendingCustomers_${currentUser.uid}`, JSON.stringify(allPendingData));
                    } else {
                        // Clear the state and localStorage when no data is found
                        setAllPending([]);
                        setFilteredAllPending([]);
                        localStorage.removeItem(`allPendingCustomers_${currentUser.uid}`);
                        console.log('No pending customers found, clearing data.');
                    }
                }
            } catch (error) {
                console.error("Error fetching pending data for all users: ", error);
            }
        };

        const fetchAllUsersActiveData = async () => {
            try {

                // Check if reps is null or empty
                if (!reps || reps.length === 0) {
                    console.log("Reps is null or empty. Returning an empty array.");
                    setallCustomers([]);
                    setFilteredallCustomers([]);
                    return;
                }

                const storedAllData = localStorage.getItem(`allCustomers_${currentUser.uid}`);
                if (storedAllData) {
                    const allData = JSON.parse(storedAllData);
                    setallCustomers(allData);
                    setFilteredallCustomers(allData);
                } else {
                    // Extract user IDs from the reps array
                    const repsIds = reps.map((rep) => rep.id);

                    let allClientsData = [];

                    // Iterate over the allowed user IDs in repsIds
                    const userPromises = repsIds.map(async (userId) => {
                        // Reference to the client's collection for this user
                        const clientsCollectionRef = collection(db, `Customers/${userId}/clients`);
                        const clientsSnapshot = await getDocs(clientsCollectionRef);

                        // Push client data to the allClientsData array
                        clientsSnapshot.forEach((clientDoc) => {
                            allClientsData.push({
                                ...clientDoc.data(),
                                id: clientDoc.id, // Add document ID
                                salesID: userId, // ADD USER ID
                            });
                        });
                    });

                    // Run all user-level fetches in parallel
                    await Promise.all(userPromises);

                    // Handle empty data scenario
                    if (allClientsData.length > 0) {
                        setallCustomers(allClientsData);
                        setFilteredallCustomers(allClientsData);
                        localStorage.setItem(`allCustomers_${currentUser.uid}`, JSON.stringify(allClientsData));
                    } else {
                        // Clear the state and localStorage when no data is found
                        setallCustomers([]);
                        setFilteredallCustomers([]);
                        localStorage.removeItem(`allCustomers_${currentUser.uid}`);
                        console.log('No clients found, clearing data.');
                    }
                }
            } catch (error) {
                console.error("Error fetching clients data for all users: ", error);
            }
        };
        
        const fetchAllUsersInActiveData = async () => {
            try {

                // Check if reps is null or empty
                if (!reps || reps.length === 0) {
                    console.log("Reps is null or empty. Returning an empty array.");
                    setAllInactive([]);
                    setFilteredAllInactive([]);
                    return;
                }

                const storedAllData = localStorage.getItem(`allInactive_${currentUser.uid}`);
                if (storedAllData) {
                    const allData = JSON.parse(storedAllData);
                    setAllInactive(allData);
                    setFilteredAllInactive(allData);
                } else {
                    // Extract user IDs from the reps array
                    const repsIds = reps.map((rep) => rep.id);

                    let allClientsData = [];

                    // Iterate over the allowed user IDs in repsIds
                    const userPromises = repsIds.map(async (userId) => {
                        // Reference to the client's collection for this user
                        const clientsCollectionRef = collection(db, `Customers/${userId}/inactive`);
                        const clientsSnapshot = await getDocs(clientsCollectionRef);

                        // Push client data to the allClientsData array
                        clientsSnapshot.forEach((clientDoc) => {
                            allClientsData.push({
                                ...clientDoc.data(),
                                id: clientDoc.id, // Add document ID
                                salesID: userId, // ADD USER ID
                            });
                        });
                    });

                    // Run all user-level fetches in parallel
                    await Promise.all(userPromises);

                    // Handle empty data scenario
                    if (allClientsData.length > 0) {
                        setAllInactive(allClientsData);
                        setFilteredAllInactive(allClientsData);
                        localStorage.setItem(`allInactive_${currentUser.uid}`, JSON.stringify(allClientsData));
                    } else {
                        // Clear the state and localStorage when no data is found
                        setAllInactive([]);
                        setFilteredAllInactive([]);
                        localStorage.removeItem(`allInactive_${currentUser.uid}`);
                        console.log('No clients found, clearing data.');
                    }
                }
            } catch (error) {
                console.error("Error fetching clients data for all users: ", error);
            }
        };

        fetchAllUsersPendingData();
        fetchAllUsersActiveData();
        fetchAllUsersInActiveData();

    }, [currentUser, reps]);



    const [updating, setUpdating] = useState(false);

    const onUpdate = async () => {
        setUpdating(true);
        const startTime = Date.now();

        // User Data
        try {

            // Fetch user document from Firestore based on the current user's UID
            const userDoc = await db.collection('Users').doc(currentUser.uid).get();
            if (userDoc.exists) {
                // Get the user's name from the document data
                const userData = userDoc.data();
                setReps(userData.reps);

                // Save user data to localStorage
                localStorage.setItem(`userData_${currentUser.uid}`, JSON.stringify(userData));
            } else {
                console.log('User document not found');
            }

        } catch (error) {
            console.error("Error fetching all pending orders", error);
        }

        // Pending
        try {
            // Extract user IDs from the reps array
            const repsIds = reps.map((rep) => rep.id);

            let allPendingData = [];

            // Iterate over the allowed user IDs in repsIds
            const userPromises = repsIds.map(async (userId) => {
                // Reference to the pending collection for this user
                const pendingCollectionRef = collection(db, `Customers/${userId}/pending`);
                const pendingSnapshot = await getDocs(pendingCollectionRef);

                // Push pending data to the allPendingData array
                pendingSnapshot.forEach((pendingDoc) => {
                    allPendingData.push({
                        ...pendingDoc.data(),
                        id: pendingDoc.id, // Add document ID
                        salesID: userId, // ADD USER ID
                    });
                });
            });

            // Run all user-level fetches in parallel
            await Promise.all(userPromises);

            // Handle empty data scenario
            if (allPendingData.length > 0) {
                setAllPending(allPendingData);
                setFilteredAllPending(allPendingData);
                localStorage.setItem(`allPendingCustomers_${currentUser.uid}`, JSON.stringify(allPendingData));
            } else {
                // Clear the state and localStorage when no data is found
                setAllPending([]);
                setFilteredAllPending([]);
                localStorage.removeItem(`allPendingCustomers_${currentUser.uid}`);
                console.log('No pending customers found, clearing data.');
            }

        } catch (error) {
            console.error("Error fetching all pending orders", error);
        }

        // Active
        try {
            // Extract user IDs from the reps array
            const repsIds = reps.map((rep) => rep.id);

            let allClientsData = [];

            // Iterate over the allowed user IDs in repsIds
            const userPromises = repsIds.map(async (userId) => {
                // Reference to the client's collection for this user
                const clientsCollectionRef = collection(db, `Customers/${userId}/clients`);
                const clientsSnapshot = await getDocs(clientsCollectionRef);

                // Push client data to the allClientsData array
                clientsSnapshot.forEach((clientDoc) => {
                    allClientsData.push({
                        ...clientDoc.data(),
                        id: clientDoc.id, // Add document ID
                        salesID: userId, // ADD USER ID
                    });
                });
            });

            // Run all user-level fetches in parallel
            await Promise.all(userPromises);

            // Handle empty data scenario
            if (allClientsData.length > 0) {
                setallCustomers(allClientsData);
                setFilteredallCustomers(allClientsData);
                localStorage.setItem(`allCustomers_${currentUser.uid}`, JSON.stringify(allClientsData));
            } else {
                // Clear the state and localStorage when no data is found
                setallCustomers([]);
                setFilteredallCustomers([]);
                localStorage.removeItem(`allCustomers_${currentUser.uid}`);
                console.log('No clients found, clearing data.');
            }

        } catch (error) {
            console.error("Error fetching active data for all users: ", error);
        }
        
        // InActive
        try {
            // Extract user IDs from the reps array
            const repsIds = reps.map((rep) => rep.id);

            let allInactiveData = [];

            // Iterate over the allowed user IDs in repsIds
            const userPromises = repsIds.map(async (userId) => {
                // Reference to the client's collection for this user
                const clientsCollectionRef = collection(db, `Customers/${userId}/inactive`);
                const clientsSnapshot = await getDocs(clientsCollectionRef);

                // Push client data to the allClientsData array
                clientsSnapshot.forEach((clientDoc) => {
                    allInactiveData.push({
                        ...clientDoc.data(),
                        id: clientDoc.id, // Add document ID
                        salesID: userId, // ADD USER ID
                    });
                });
            });

            // Run all user-level fetches in parallel
            await Promise.all(userPromises);

            // Handle empty data scenario
            if (allInactiveData.length > 0) {
                setAllInactive(allInactiveData);
                setFilteredAllInactive(allInactiveData);
                localStorage.setItem(`allInactive_${currentUser.uid}`, JSON.stringify(allInactiveData));
            } else {
                // Clear the state and localStorage when no data is found
                setAllInactive([]);
                setFilteredAllInactive([]);
                localStorage.removeItem(`allInactive_${currentUser.uid}`);
                console.log('No clients found, clearing data.');
            }

        } catch (error) {
            console.error("Error fetching active data for all users: ", error);
        }

        const endTime = Date.now();
        const timeElapsed = (endTime - startTime) /1000;
        setUpdating(false);
        
        triggerNotification("allCustUpdated");
        
        if (alertsOn) {
            alert(`Orders Updated in ${timeElapsed} secs`)
        }
    };







    const handlePendingSearch = (query) => {
        setSearchQuery(query);
        const filtered = allPending.filter((customer) =>
            customer.name.toLowerCase().includes(query.toLowerCase())
        );
        setFilteredAllPending(filtered);
    }

    const handleActiveSearch = (query) => {
        setSearchQuery(query);
        const filtered = allCustomers.filter((customer) =>
            customer.name.toLowerCase().includes(query.toLowerCase()) ||
            customer.customerCode.toLowerCase().includes(query.toLowerCase()) ||
            parseFloat(customer.customerType).toString().toLowerCase().includes(query.toLowerCase())
        );
        setFilteredallCustomers(filtered);
    }
    
    
    const handleInactiveSearch = (query) => {
        setSearchQuery(query);
        const filtered = allInactive.filter((customer) =>
            customer.name.toLowerCase().includes(query.toLowerCase()) ||
            customer.customerCode.toLowerCase().includes(query.toLowerCase()) ||
            parseFloat(customer.customerType).toString().toLowerCase().includes(query.toLowerCase())
        );
        setFilteredAllInactive(filtered);
    }

    // Export customers to Excel (filtered by allowed states)
    const exportCustomersToExcel = async () => {
        try {
            const XLSX = await import('xlsx');

            // Allowed states
            const allowedStates = ['FL', 'GA', 'TN', 'MS', 'AL'];

            // Combine all customer lists
            const combinedCustomers = [
                ...allCustomers,
                // ...allPending,
                // ...allInactive
            ];

            // Filter + map data
            const exportData = combinedCustomers
                .filter(customer => allowedStates.includes(customer.state))
                .map(customer => ({
                    Name: customer.name || '',
                    Email: customer.contactEmail || '',
                    Phone: customer.contactPhone || ''
                }));

            if (exportData.length === 0) {
                alert('No customers found for selected states.');
                return;
            }

            // Create worksheet
            const worksheet = XLSX.utils.json_to_sheet(exportData);

            // Create workbook
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Customers');

            // Export file
            XLSX.writeFile(workbook, 'Filtered_Customers.xlsx');

        } catch (error) {
            console.error('Error exporting customers:', error);
        }
    };
    
    
    const filtering = (e) => {
        const selected = e.target.value;
    
        const getFiltered = (list) => {
            if (selected === '') return list;
            if (selected === 'NULL') {
                return list.filter(customer => !customer.hasOwnProperty('industry'));
            }
            return list.filter(customer => customer.industry === selected);
        };
    
        if (status === 'A') {
            setFilteredallCustomers(getFiltered(allCustomers));
        } else if (status === 'P') {
            setFilteredAllPending(getFiltered(allPending));
        } else if (status === 'I') {
            setFilteredAllInactive(getFiltered(allInactive));
        }
    };
    
    const filteringPricing = (e) => {
        const selected = e.target.value;
    
        const getFiltered = (list) => {
            if (selected === '') return list;
            if (selected === 'NULL') {
                return list.filter(customer => !customer.hasOwnProperty('priceType'));
            }
            return list.filter(customer => customer.priceType === selected);
        };
    
        if (status === 'A') {
            setFilteredallCustomers(getFiltered(allCustomers));
        } else if (status === 'P') {
            setFilteredAllPending(getFiltered(allPending));
        } else if (status === 'I') {
            setFilteredAllInactive(getFiltered(allInactive));
        }
    };
    
    const filteringRep = (e) => {
        const selected = e.target.value;
    
        const getFiltered = (list) => {
            if (selected === '') return list;
            if (selected === 'NULL') {
                return list.filter(customer => !customer.hasOwnProperty('salesRep'));
            }
            return list.filter(customer => customer.salesRep === selected);
        };
    
        if (status === 'A') {
            setFilteredallCustomers(getFiltered(allCustomers));
        } else if (status === 'P') {
            setFilteredAllPending(getFiltered(allPending));
        } else if (status === 'I') {
            setFilteredAllInactive(getFiltered(allInactive));
        }
    };
   

    const statusMap = {
        A: {
            customers: filteredallCustomers,
            emptyTitle: "Your Reps Have No Active Customers",
            emptyDescription: "You have to add pending customers to the ERP",
        },
        P: {
            customers: fileredAllPending,
            emptyTitle: "Your Reps Have No Pending Customers",
            emptyDescription: "You Must Wait For your Reps to Onboard New Customers",
        },
        I: {
            customers: fileredAllInactive,
            emptyTitle: "Your Reps Have No Inactive Customers",
            emptyDescription: "These are customers that your reps have label as inactive",
        },
    };

    const currentStatus = statusMap[status] || {};
    
    
    
    const [accountView, setAccountView] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    
    const toggleAccountView = () => {
        if (!selectedCustomer) {
            setShowPopup(true); // Show popup if no customer is selected
        } else {
            setAccountView(!accountView);
        }
    };
    
    
    
    // Function to handle both accountView and selected customer
    const handleViewAccount = (customer) => {
        setAccountView(true);
        setSelectedCustomer(customer);
    };
    
    
    
    const [adding, setAdding] = useState(false);
    
    const [customerCode, setCustomerCode] = useState("");
    const [name, setName] = useState("");
    const [priceType, setPriceType] = useState("");
    const [industry, setIndustry] = useState("");
    const [customerType, setCustomerType] = useState("");

    const [customerEmail, setCustomerEmail] = useState("");
    const [customerPhone, setCustomerPhone] = useState("");
    const [contactName, setContactName] = useState("");
    
    const [city, setCity] = useState("");
    const [stCode , setStCode] = useState("");
    
    const [fullname, setFullname] = useState("");
    const [salesCode, setSalesCode] = useState("");
    const [salesEmail, setSalesEmail] = useState("");
    const [salesID, setSalesID] = useState("");
    
    const clearForm = () => {
        setCustomerCode('');
        setName('');
        setPriceType('');
        setCustomerType('');
        setCustomerEmail('');
        setContactName('');
        setCustomerPhone('');
        
        setCity('');
        setStCode('');
        
        setSalesID('');
        setSalesCode('');
        setFullname('');
        setSalesEmail('');
    }

    const onSubmitMain = async (e) => {
        e.preventDefault();

        const collectionRef = db.collection("Customers").doc(salesID).collection("clients");

        const parsedFormData = {

            // Sales Info
            customerCode: customerCode,
            name: name,
            priceType: priceType,
            industry: industry,
            customerType: parseFloat(customerType),
            contactPhone: customerPhone,
            contactEmail: customerEmail,
            contactName: contactName,
            
            city: city,
            state: stCode,

            // Sales Info
            salesRep: fullname,
            salesCode: salesCode,
            salesEmail: salesEmail,

            status: "active",
        };

        try {
            await collectionRef.add(parsedFormData);
            // handleNextTab();
            if (alertsOn) {
                alert(`Customer Added to the Cloud`); // Optional: alert for user feedback
            }

            clearForm();
            
            onUpdate();
            
            setAdding(false);
            
            triggerNotification("custAdded");

        } catch (error) {
            console.error('Error registering client:', error.message);
            triggerNotification("custAddedError");
            // setIsRegistered(false);
        }
    };
    
    const [users, setUsers] = useState([]);
    
    useEffect(() => {
        const fetchUsers = async () => {
            const usersCollection = collection(db, 'Users');
            const userSnapshot = await getDocs(usersCollection);
            const userList = userSnapshot.docs.map(doc => ({
                salesID: doc.id, // UID from Firestore document ID
                salesRep: `${doc.data().fname} ${doc.data().lname}`, // Combining fname and lname into a single name field
                accountLevel: doc.data().accountLevel,
                salesEmail: doc.data().email,
                salesCode: doc.data().salesCode,
            }));
            setUsers(userList);
        };
        fetchUsers();
    }, []);
    
    const handleSelectChange = (event) => {
        const selectedUser = event.target.value ? JSON.parse(event.target.value) : null;
    
        if (selectedUser) {
            setFullname(selectedUser.salesRep || "");
            setSalesCode(selectedUser.salesCode || "");
            setSalesEmail(selectedUser.salesEmail || "");
            setSalesID(selectedUser.salesID || "");
        } else {
            setFullname("");
            setSalesCode("");
            setSalesEmail("");
            setSalesID("");
        }
    };
    
    
    
    const [secondaryNavigation, setSecondaryNavigation] = useState([
      // { name: 'Dashboard', href: '#', current: false },
      { name: 'Active', href: 'A', current: false },
      { name: 'Pending', href: 'P', current: true },
      { name: 'Inactive', href: 'I', current: false },
    ]);
    
    

    return (
        <div className={`${darkMode ? 'text-white' : 'text-black'} flex h-[calc(100dvh-3.5rem)] w-full transition-all duration-500 ease-in-out`}>

            {updating && (
                <Loading/>
            )}
            
            
            {/*Redesign*/}
            <>
        
              
                <main className={`animate-fadeIn h-[calc(100dvh-3.5rem)] overflow-y-auto scrollbar-hide z-20 w-full ${darkMode ? 'border-neutral-700 bg-darkMainColor text-white' : 'bg-white text-black'}`}>
                
                    {/*Header*/}
                    <div className="relative isolate overflow-auto">
                      
                        {/* Secondary navigation */}
                        <header className={`py-1 border-b ${darkMode ? 'border-neutral-700' : ''}`}>
                            
                            <div className="mx-auto flex flex-wrap items-center gap-3 px-3 sm:flex-nowrap">
                                
                                <CustomButton
                                    icon={ArrowPathIcon}
                                    text={`Sync`}
                                    onClick={onUpdate}
                                    color={`green`}
                                />
                                
                                
                                <div className={`flex flex-col`}>
                                    <h1 className="font-semibold">Customer Management</h1>
                                    <h1 className="text-xxs -mt-0.5 text-gray-500">Customers from your Sale Rep's</h1>
                                </div>
                                
                                
                                <div className={`order-last flex w-full gap-x-8 font-semibold sm:order-none sm:w-auto sm:border-l ${darkMode ? 'sm:border-neutral-700' : ''} sm:pl-6 sm:text-sm/7`}>
                                    {secondaryNavigation.map((item) => (
                                        <div
                                            onClick={() => {
                                            setStatus(item.href);
                                                setSecondaryNavigation((nav) =>
                                                  nav.map((navItem) => ({
                                                    ...navItem,
                                                    current: navItem.href === item.href,
                                                  }))
                                                );
                                            }}
                                            key={item.name}
                                            className={`${item.current ? 'text-green-600 underline text-sm tracking-wide' : 'text-gray-500 text-xs'} duration-500  cursor-pointer`}
                                        >
                                            {item.name}
                                        </div>
                                    ))}
                                </div>
                                
                                {/*Buttons*/}
                                <div className={`ml-auto gap-x-3 flex items-center`}>
                                    
                                    {/*<ActionPillButton*/}
                                    {/*    label={`Export Customers`}*/}
                                    {/*    onClick={exportCustomersToExcel}*/}
                                    {/*    color={`green`}*/}
                                    {/*    icon={DocumentArrowDownIcon}*/}
                                    {/*/>*/}
                                    
                                    <CustomButton
                                        icon={accountView ? QueueListIcon : IdentificationIcon}
                                        text={accountView ? 'Return To List' : 'Account View'}
                                        onClick={toggleAccountView}
                                        color={`blue`}
                                        disabled={!selectedCustomer}
                                    />
                                    
                                    <CustomButton
                                        icon={PlusCircleIcon}
                                        text={`Add Customer`}
                                        onClick={() => setAdding(true)}
                                        color={`green`}
                                    />
                                    
                                </div>
                                
                            </div>
                            
                        </header>
            
                        {/* Stats */}
                        <div className={`border-b ${accountView ? 'hidden' : ''} ${darkMode ? 'bg-gray-700/10 border-b-neutral-700 lg:border-t-neutral-700' : 'bg-neutral-300/50 border-b-gray-900/10 lg:border-t-gray-900/5'}`}>
                            <dl className="mx-auto w-full">
                                
                                {(() => {
                                    const stats = [
                                      {
                                        label: "Total Customers",
                                        value: `${numberWithCommas(allCustomers.length + allPending.length)}`,
                                        previous: "All Pending and Active customers",
                                        icon: UserGroupIcon,
                                        color: 'text-green-600',
                                        bgColor: 'bg-green-600',
                                        positive: true,
                                      },
                                      {
                                        label: "Active",
                                        value: `${numberWithCommas(allCustomers.length)}`,
                                        previous: "All Customers that can place orders",
                                        icon: CheckCircleIcon,
                                        color: 'text-green-600',
                                        bgColor: 'bg-green-600',
                                        stat: "A",
                                        positive: true,
                                      },
                                      {
                                        label: "Pending",
                                        value: `${numberWithCommas(allPending.length)}`,
                                        previous: "All Customers waiting to be onboarded",
                                        icon: MinusCircleIcon,
                                        color: 'text-yellow-600',
                                        bgColor: 'bg-yellow-600',
                                        stat: "P",
                                        positive: false,
                                      },
                                      {
                                        label: "Inactive",
                                        value: numberWithCommas(allInactive.length),
                                        previous: "Customers that are not active",
                                        icon: StopCircleIcon,
                                        color: 'text-red-600',
                                        bgColor: 'bg-red-600',
                                        stat: "I",
                                        positive: false,
                                      },
                                    ];
            
                                    return (
                                      <div className={`grid grid-cols-4 overflow-hidden divide-x ${darkMode ? 'divide-neutral-700' : ''}`}>
                                        {stats.map((stat, index) => {
            
                                            const isActive = stat.stat === status;
                                            
                                          return (
                                            <a
                                              key={index}
                                              href="#"
                                              className={`block p-2 relative ${isActive ? `${stat.bgColor} bg-opacity-10` : ''}`}
                                            >
                                              <div className="flex flex-col lg:flex-row gap-y-1 gap-x-3">
                                                <stat.icon className={`shrink-0 size-5 text-muted-foreground stroke-2 ${stat.color}`} />
            
                                                <div className="grow">
            
                                                    {/*Title*/}
                                                  <p className="text-xs uppercase font-medium text-gray-500">
                                                    {stat.label}
                                                  </p>
            
                                                    {/*Value*/}
                                                  <h3 className="text-xl -mt-1 sm:text-2xl font-semibold">
                                                    {stat.value}
                                                  </h3>
            
                                                    {/*Previous*/}
                                                  <div className="flex -mt-1 justify-between items-center text-gray-500">
                                                    
                                                    <p className="text-xs">
                                                      <span className="italic">
                                                        {stat.previous}
                                                      </span>
                                                    </p>
                                                    
                                                  </div>
            
                                                </div>
                                              </div>
                                            </a>
                                          );
                                        })}
                                      </div>
                                    );
                                })()}
                                
                            </dl>
                        </div>
                        
                      
                    </div>
        
                    
                    
                    {/*Table*/}
                    <div className={`${accountView ? 'hidden' : ''}`}>
                        
                        
                        {/* Recent activity table */}
                        <div>
                            
                            {/*Search Bar*/}
                            <div className={`w-full items-center py-1 gap-x-3 pr-3 h-fit flex ${darkMode ? 'text-white' : 'text-black'}`}>
                                
                                {/*Search Bar*/}
                                <div className="w-full h-fit text-sm">
                                    
                                    
                                    <div className={`${darkMode ? 'text-white' : 'text-black'} w-full flex items-center justify-center hover:scale-y-105`}>
                                        <CustomerSearchInput
                                            status={status}
                                            searchQuery={searchQuery}
                                            setSearchQuery={setSearchQuery}
                                            onSearch={
                                                status === "A" ? handleActiveSearch :
                                                status === "P" ? handlePendingSearch :
                                                status === "I" ? handleInactiveSearch :
                                                () => {
                                                }
                                            }
                                            darkMode={darkMode}
                                        />
                                    </div>
                                
                                
                                </div>
                                
                                {/*Pricing Type*/}
                                <div className="w-fit text-sm">
                                    
                                    <div className="flex flex-col w-fit mr-3 h-full">
                                        {/*<div className="text-sm mb-1">Industry</div>*/}
                                        
                                        <Select
                                            className={`bg-transparent ${darkMode ? 'text-white' : 'text-black'} focus:outline-none h-full drop-shadow shadow-gray-900 py-1 px-4 rounded-md hover:scale-y-105`}
                                            // onChange={(e) => filterCustomersByIndustry(e.target.value)}
                                            onChange={filteringPricing}
                                        >
                                            <option value="">Select Pricing Type...</option>
                                            <option value="NULL">No Pricing Type Set</option>
                                            <option value="NE">North East</option>
                                            <option value="SE">South East</option>
                                            <option value="AG">Agriculture</option>
                                            <option value="DIST1">Distributor 1</option>
                                            <option value="DIST2">Distributor 2</option>
                                        </Select>
                                    </div>
                                
                                </div>
                                
                                {/*Industry*/}
                                <div className="w-fit text-sm">
                                    
                                    <div className="flex flex-col w-fit mr-3 h-full">
                                        {/*<div className="text-sm mb-1">Industry</div>*/}
                                        
                                        <Select
                                            className={`bg-transparent ${darkMode ? 'text-white' : 'text-black'} focus:outline-none h-full drop-shadow shadow-gray-900 py-1 px-4 rounded-md hover:scale-y-105`}
                                            // onChange={(e) => filterCustomersByIndustry(e.target.value)}
                                            onChange={filtering}
                                        >
                                            <option value="">Select Industry...</option>
                                            <option value="BOTANICAL">Botanical Garden</option>
                                            <option value="DISTRIB">Product Distributor</option>
                                            <option value="EMPLOYEE">PFC-EMPLOYEE</option>
                                            <option value="FARM">Agriculture - Farm</option>
                                            <option value="GOLF">Golf Course</option>
                                            <option value="GOVERNMENT">Government Entity</option>
                                            <option value="INDUSTRIAL">Industrial</option>
                                            <option value="LAWNTREE">Lawn Care & Tree Care</option>
                                            <option value="MRSALE">One Time Purchase Expected</option>
                                            <option value="MUNICIPAL">Municipal/ School</option>
                                            <option value="Non-Profit">Non-Profit Account</option>
                                            <option value="OTHER">Other</option>
                                            <option value="SPORTFLD">Sportsfield</option>
                                            <option value="NULL">No Industry Set</option>
                                        </Select>
                                    </div>
                                
                                </div>
                                
                                {/*Sales Rep*/}
                                <div className="w-fit text-sm">
                                    
                                    <div className="flex flex-col w-fit mr-3 h-full">
                                        {/*<div className="text-sm mb-1">Pricing Type</div>*/}
                                        <Select
                                            className={`bg-transparent ${darkMode ? 'text-white' : 'text-black'} focus:outline-none h-full drop-shadow shadow-gray-900 py-1 px-4 rounded-md hover:scale-y-105`}
                                            onChange={filteringRep}
                                        >
                                            <option value="">Select Sales Rep...</option>
                                            
                                            {reps.map((rep, index) => {
                                                const label = rep.name; // Get label from mapping object
                                                return label ? (
                                                    <option key={index} value={rep.name}>
                                                        {label}
                                                    </option>
                                                ) : null; // Exclude options with no matching label
                                            })}
                                        </Select>
                                        
                                        
                                    </div>
                                
                                </div>
                            
                            
                            </div>
                            
                            
                            {/*Table*/}
                            <div className={`overflow-hidden border-t ${darkMode ? 'border-neutral-700' : 'border-gray-100'}`}>
                                <div className="mx-auto">
                                    <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
                                        
                                        
                                        {/*CUSTOMERS MAP*/}
                                        <div className={`overflow-y-auto scrollbar-hide ${darkMode ? 'text-white bg-darkMainColor' : 'text-black bg-white'} rounded-lg h-[calc(100dvh-15rem)] scroll`}>
                                            
                                            <CustomerSection
                                                customers={currentStatus.customers || []}
                                                emptyTitle={currentStatus.emptyTitle}
                                                emptyDescription={currentStatus.emptyDescription}
                                                darkMode={darkMode}
                                                onUpdate={onUpdate}
                                                // setAccountView={setAccountView}
                                                handleViewAccount={handleViewAccount}
                                                isShared={currentStatus.isShared}
                                                plant={false}
                                            />
                                        
                                        </div>
                                    
                                    
                                    </div>
                                </div>
                            </div>
                            
                        </div>
                    
                    
                    </div>
                    
                    
                    {accountView && (
                        
                        <div className={`px-2 pb-2`}>
                            <AccountView
                                customer={selectedCustomer}
                                onUpdate={onUpdate}
                                plant={true}
                            />
                        </div>
                    
                    )}
                
                
                </main>
            
            </>
            
            
            
            <Dialog open={showPopup} onClose={setShowPopup} className="relative z-50">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500/50 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
              />
        
              <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                  <DialogPanel
                    transition
                    className={`relative transform overflow-hidden rounded-lg ${darkMode ? 'bg-darkMainColor text-white' : 'bg-white text-black'} px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95`}
                  >
                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-red-600/15 sm:mx-0 sm:size-10">
                        <ExclamationTriangleIcon aria-hidden="true" className="size-6 text-red-600" />
                      </div>
                      <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                        <DialogTitle as="h3" className="text-base font-semibold">
                          No Customer Selected
                        </DialogTitle>
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">
                                Click the expand button on any customer account in the list below to open the Account View. Once selected, the Account View will automatically display.                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <Button
                        type="button"
                        onClick={() => setShowPopup(false)}
                        className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                      >
                        I understand
                      </Button>
                      <Button
                        type="button"
                        data-autofocus
                        onClick={() => setShowPopup(false)}
                        className={`mt-3 inline-flex w-full justify-center rounded-md ${darkMode ? 'bg-darkBgColor' : 'bg-white ring-1 ring-inset ring-gray-300'} px-3 py-2 text-sm font-semibold shadow-sm hover:bg-opacity-50 sm:mt-0 sm:w-auto`}
                      >
                        Cancel
                      </Button>
                    </div>
                  </DialogPanel>
                </div>
              </div>
            </Dialog>
            
            <Dialog open={adding} onClose={setAdding} className="relative z-50">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500/50 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
                />
        
                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <DialogPanel
                            transition
                            className={`relative transform overflow-hidden rounded-lg ${darkMode ? 'bg-darkMainColor text-white' : 'bg-white text-black'} px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-5xl sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95`}
                        >
                            {/*Header*/}
                            <div className="sm:flex sm:items-start">
                                <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-green-600/15 sm:mx-0 sm:size-10">
                                    <UserPlusIcon aria-hidden="true" className="size-6 text-green-600" />
                                </div>
                                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                                    <DialogTitle as="h3" className="text-base font-semibold">
                                        Add Customer to Rep Account
                                    </DialogTitle>
                                    <div className="mt-2 w-full">
                                        <p className="text-sm text-gray-500">
                                            This form will add a new customer to the reps account. You must choose one
                                            of the reps from the drop down! Once the account it set up you may go into the expanded view to fill out further information!
                                        </p>
                                        
                                        
                                        <form className="p-5 space-y-6"
                                              onSubmit={onSubmitMain}
                                              method="POST">
                                            
                                            
                                            {/*Row 1*/}
                                            <div className="grid gap-3 mb-3 grid-cols-2 w-full">
                                                
                                                {/*Sales Rep*/}
                                                <div className={`relative col-span-full`}>
                                                    <label
                                                        className={`absolute -top-2 left-2 inline-block rounded-lg ${darkMode ? 'bg-darkMainColor' : 'bg-white text-gray-900'} px-1 text-xs font-medium`}
                                                    >
                                                        Sales Rep
                                                    </label>
                                                    <Select
                                                        type="text"
                                                        onChange={handleSelectChange}
                                                        placeholder={`Customer Code`}
                                                        className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white outline-gray-500' : 'bg-white text-gray-900 outline-gray-300'} px-3 py-2.5 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                    >
                                                        <option value="">Select a Sales Rep</option>
                                                        {users
                                                            .filter(user => user.accountLevel === "S" || user.accountLevel === "A" || user.accountLevel === "DIST")
                                                            .map(user => (
                                                                <option key={user.salesCode} value={JSON.stringify(user)}>
                                                                    {user.salesRep}
                                                                </option>
                                                            ))}
                                                    </Select>
                                                </div>
                                                
                                                {/*Customer Code*/}
                                                <div className={`relative col-span-full flex justify-between text-gray-500 text-sm`}>
                                                    <div className={``}>Sales Rep: {fullname}</div>
                                                    <div className={``}>Sales Code: {salesCode}</div>
                                                    <div className={``}>Sales Email: {salesEmail}</div>
                                                </div>
                                            
                                            </div>
                                            
                                            {/*Row 2*/}
                                            <div className="grid gap-3 mb-3 grid-cols-2 w-full">
                                                
                                                {/*Customer Code*/}
                                                <div className={`relative col-span-1`}>
                                                    <label
                                                        className={`absolute -top-2 left-2 inline-block rounded-lg ${darkMode ? 'bg-darkMainColor' : 'bg-white text-gray-900'} px-1 text-xs font-medium`}
                                                    >
                                                        Customer Code
                                                    </label>
                                                    <Input
                                                        type="text"
                                                        value={customerCode}
                                                        onChange={(e) => setCustomerCode(e.target.value)}
                                                        placeholder={`Customer Code`}
                                                        className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white outline-gray-500' : 'bg-white text-gray-900 outline-gray-300'} px-3 py-2 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                    />
                                                </div>
                                                
                                                {/*Customer Name*/}
                                                <div className={`relative col-span-1`}>
                                                    <label
                                                        className={`absolute -top-2 left-2 inline-block rounded-lg ${darkMode ? 'bg-darkMainColor' : 'bg-white text-gray-900'} px-1 text-xs font-medium`}
                                                    >
                                                        Customer Name
                                                    </label>
                                                    <Input
                                                        id="code"
                                                        name="code"
                                                        type="text"
                                                        value={name}
                                                        onChange={(e) => setName(e.target.value)}
                                                        placeholder={`Customer Name`}
                                                        className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white outline-gray-500' : 'bg-white text-gray-900 outline-gray-300'} px-3 py-2 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                    />
                                                </div>
                                            
                                            </div>
                                            
                                            {/*Row 3*/}
                                            <div className="grid gap-3 mb-3 grid-cols-3">
                                                
                                                {/*Price Type*/}
                                                <div className={`relative col-span-1`}>
                                                    <label
                                                        className={`absolute -top-2 left-2 inline-block rounded-lg ${darkMode ? 'bg-darkMainColor' : 'bg-white text-gray-900'} px-1 text-xs font-medium`}
                                                    >
                                                        Pricing Type
                                                    </label>
                                                    <Select
                                                        type="text"
                                                        value={priceType}
                                                        onChange={(e) => setPriceType(e.target.value)}
                                                        className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white outline-gray-500' : 'bg-white text-gray-900 outline-gray-300'} px-3 py-2.5 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                    >
                                                        <option value="">Select Pricing Type...</option>
                                                        <option value="AG">Agriculture</option>
                                                        <option value="DIST1">Distributor NE/INTL</option>
                                                        <option value="DIST2">Distributor SE/CAN</option>
                                                        <option value="NE">Northeast</option>
                                                        <option value="SE">Southeast</option>
                                                    </Select>
                                                </div>
                                                
                                                {/*Industry*/}
                                                <div className={`relative col-span-1`}>
                                                    <label
                                                        className={`absolute -top-2 left-2 inline-block rounded-lg ${darkMode ? 'bg-darkMainColor' : 'bg-white text-gray-900'} px-1 text-xs font-medium`}
                                                    >
                                                        Industry
                                                    </label>
                                                    <Select
                                                        type="text"
                                                        value={industry}
                                                        onChange={(e) => setIndustry(e.target.value)}
                                                        className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white outline-gray-500' : 'bg-white text-gray-900 outline-gray-300'} px-3 py-2.5 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                    >
                                                        <option value="">Select Industry...</option>
                                                        <option value="BOTANICAL">Botanical Garden</option>
                                                        <option value="DISTRIB">Product Distributor</option>
                                                        <option value="EMPLOYEE">PFC-EMPLOYEE</option>
                                                        <option value="FARM">Agriculture - Farm</option>
                                                        <option value="GOLF">Golf Course</option>
                                                        <option value="GOVERNMENT">Government Entity</option>
                                                        <option value="INDUSTRIAL">Industrial</option>
                                                        <option value="LAWNTREE">Lawn Care & Tree Care</option>
                                                        <option value="MRSALE">One Time Purchase Expected</option>
                                                        <option value="MUNICIPAL">Municipal/ School</option>
                                                        <option value="Non-Profit">Non-Profit Account</option>
                                                        <option value="OTHER">Other</option>
                                                        <option value="SPORTFLD">Sportsfield</option>
                                                    </Select>
                                                </div>
                                                
                                                {/*Customer Type*/}
                                                <div className={`relative col-span-1`}>
                                                    <label
                                                        className={`absolute -top-2 left-2 inline-block rounded-lg ${darkMode ? 'bg-darkMainColor' : 'bg-white text-gray-900'} px-1 text-xs font-medium`}
                                                    >
                                                        Customer Type
                                                    </label>
                                                    <Select
                                                        type="text"
                                                        value={customerType}
                                                        onChange={(e) => setCustomerType(e.target.value)}
                                                        className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white outline-gray-500' : 'bg-white text-gray-900 outline-gray-300'} px-3 py-2.5 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                    >
                                                        <option value="">Select Customer Type...</option>
                                                        <option value="5">Level 1 [5%]</option>
                                                        <option value="10">Level 2 [10%]</option>
                                                        <option value="15">Level 3 [15%]</option>
                                                        <option value="20">Level 4 [20%]</option>
                                                        <option value="0">GLG</option>
                                                        <option value="33">33% Off</option>
                                                    </Select>
                                                </div>
                                                
                                            </div>
                                            
                                            {/*Row 4*/}
                                            <div className="grid gap-3 mb-3 grid-cols-3">
                                            
                                            
                                            {/*Contact Name*/}
                                                <div className={`relative col-span-1`}>
                                                    <label
                                                        className={`absolute -top-2 left-2 inline-block rounded-lg ${darkMode ? 'bg-darkMainColor' : 'bg-white text-gray-900'} px-1 text-xs font-medium`}
                                                    >
                                                        Contact Name
                                                    </label>
                                                    <Input
                                                        type="text"
                                                        value={contactName}
                                                        onChange={(e) => setContactName(e.target.value)}
                                                        placeholder={`Contact Name`}
                                                        className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white outline-gray-500' : 'bg-white text-gray-900 outline-gray-300'} px-3 py-2 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                    />
                                                </div>
                                                
                                                {/*Contact Email*/}
                                                <div className={`relative col-span-1`}>
                                                    <label
                                                        className={`absolute -top-2 left-2 inline-block rounded-lg ${darkMode ? 'bg-darkMainColor' : 'bg-white text-gray-900'} px-1 text-xs font-medium`}
                                                    >
                                                        Contact Email
                                                    </label>
                                                    <Input
                                                        type="text"
                                                        value={customerEmail}
                                                        onChange={(e) => setCustomerEmail(e.target.value)}
                                                        placeholder={`Contact Email`}
                                                        className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white outline-gray-500' : 'bg-white text-gray-900 outline-gray-300'} px-3 py-2 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                    />
                                                </div>
                                                
                                                {/*Contact Phone*/}
                                                <div className={`relative col-span-1`}>
                                                    <label
                                                        className={`absolute -top-2 left-2 inline-block rounded-lg ${darkMode ? 'bg-darkMainColor' : 'bg-white text-gray-900'} px-1 text-xs font-medium`}
                                                    >
                                                        Contact Phone
                                                    </label>
                                                    <Input
                                                        type="text"
                                                        value={customerPhone}
                                                        onChange={(e) => setCustomerPhone(e.target.value)}
                                                        placeholder={`Contact Phone`}
                                                        className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white outline-gray-500' : 'bg-white text-gray-900 outline-gray-300'} px-3 py-2 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                    />
                                                </div>
                                                
                                            
                                            </div>
                                            
                                            {/*Row 5*/}
                                            <div className="grid gap-3 mb-3 grid-cols-2">
                                                
                                                {/*City*/}
                                                <div className={`relative col-span-1`}>
                                                    <label
                                                        className={`absolute -top-2 left-2 inline-block rounded-lg ${darkMode ? 'bg-darkMainColor' : 'bg-white text-gray-900'} px-1 text-xs font-medium`}
                                                    >
                                                        City
                                                    </label>
                                                    <Input
                                                        type="text"
                                                        value={city}
                                                        onChange={(e) => setCity(e.target.value)}
                                                        placeholder={`City`}
                                                        className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white outline-gray-500' : 'bg-white text-gray-900 outline-gray-300'} px-3 py-2 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                    />
                                                </div>
                                                
                                                {/*State*/}
                                                <div className={`relative col-span-1`}>
                                                    <label
                                                        className={`absolute -top-2 left-2 inline-block rounded-lg ${darkMode ? 'bg-darkMainColor' : 'bg-white text-gray-900'} px-1 text-xs font-medium`}
                                                    >
                                                        State
                                                    </label>
                                                    <Select
                                                        type="text"
                                                        value={stCode}
                                                        onChange={(e) => setStCode(e.target.value)}
                                                        className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white outline-gray-500' : 'bg-white text-gray-900 outline-gray-300'} px-3 py-2.5 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                    >
                                                        {Object.entries(stateMapping).map(([abbr, name]) => (
                                                            <option key={abbr} value={abbr}>
                                                              {name}
                                                            </option>
                                                        ))}
                                                    </Select>
                                                </div>
                                            
                                            </div>
                                        
                                        </form>
                                    
                                    
                                    </div>
                                </div>
                            </div>
                            
                            {/*Buttons*/}
                            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                <Button
                                    type="button"
                                    // onClick={() => setAdding(false)}
                                    onClick={onSubmitMain}
                                    className="inline-flex w-full justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-opacity-50 sm:ml-3 sm:w-auto"
                                >
                                    Add Customer
                                </Button>
                                <Button
                                    type="button"
                                    data-autofocus
                                    onClick={() => setAdding(false)}
                                    className={`mt-3 inline-flex w-full justify-center rounded-md ${darkMode ? 'bg-darkBgColor' : 'bg-white ring-1 ring-inset ring-gray-300'} px-3 py-2 text-sm font-semibold shadow-sm hover:bg-opacity-50 sm:mt-0 sm:w-auto`}
                                >
                                    Cancel
                                </Button>
                            </div>
                        
                        </DialogPanel>
                    </div>
                </div>
            </Dialog>
            

        </div>
    )
};


export default PlantCustomers;
